import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ChevronLeft, ArrowUpRight, Check, X } from "lucide-react";
import { Footer } from "@/components/landing/Footer";
import { Wordmark } from "@/components/landing/Wordmark";
import { GUIDES } from "@/data/academy";
import { markDone } from "@/pages/LearnHub";
import { useSeo } from "@/lib/seo";

const Quiz = ({ items }) => {
  const [pick, setPick] = useState({});
  return (
    <div data-testid="guide-quiz" className="mt-14 border border-white/[0.07] bg-[#0B0C0F] p-6 md:p-8">
      <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#5A6170]">Quick check</p>
      {items.map((it, qi) => (
        <div key={it.q} className="mt-6">
          <p className="text-[16px] text-[#ECEBE4]">{it.q}</p>
          <div className="mt-3 grid gap-2">
            {it.a.map((a, ai) => {
              const chosen = pick[qi] === ai, right = ai === it.c, show = pick[qi] != null;
              return (
                <button key={a} data-testid={`quiz-${qi}-opt-${ai}`} disabled={show} onClick={() => setPick({ ...pick, [qi]: ai })}
                  className={`flex items-center justify-between border px-4 py-3 text-left text-[14px] transition-colors ${show && right ? "border-[#BACD1C]/50 text-[#ECEBE4]" : show && chosen ? "border-[#FF4D5A]/50 text-[#ECEBE4]" : "border-white/[0.08] text-[#9DA3AE] hover:border-white/25"}`}>
                  {a}{show && right && <Check size={15} className="text-[#BACD1C]" />}{show && chosen && !right && <X size={15} className="text-[#FF4D5A]" />}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default function Guide() {
  const { slug } = useParams();
  const g = GUIDES.find((x) => x.slug === slug);
  useSeo({ title: g ? `${g.t} guide | TradrQuant Academy` : "TradrQuant Academy", description: g?.what || "", path: `/learn/guide/${slug}` });
  useEffect(() => { window.scrollTo(0, 0); if (g) markDone(`guide:${g.slug}`); }, [g]);
  if (!g) return <Navigate to="/learn" replace />;
  const more = GUIDES.filter((x) => x.kind === g.kind && x.slug !== g.slug).slice(0, 3);
  return (
    <div data-testid="guide-page" className="min-h-screen bg-[#08090B] text-[#ECEBE4]">
      <header className="border-b border-white/[0.07]">
        <div className="mx-auto flex h-14 max-w-[1100px] items-center justify-between px-5 md:px-10">
          <Link to="/"><Wordmark /></Link>
          <Link to="/learn" data-testid="guide-back-btn" className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.18em] text-[#7D8390] hover:text-[#ECEBE4]"><ChevronLeft size={14} />Academy</Link>
        </div>
      </header>
      <article className="mx-auto max-w-[900px] px-5 pb-24 pt-14 md:px-10 md:pt-20">
        <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-[#7D8390]">{g.kind === "tool" ? "Tool guide" : "Routine"} · {g.mins} min read</p>
        <h1 className="mt-4 font-display text-4xl font-semibold leading-[1.02] tracking-[-0.04em] sm:text-5xl">{g.t}</h1>
        <p className="mt-3 text-lg text-[#7D8390]">{g.sub}</p>
        <div className="mt-10 overflow-hidden border border-white/[0.08]"><img src={g.img} alt={g.t} width={1600} height={900} className="block h-auto w-full" /></div>
        <h2 className="mt-12 font-display text-2xl font-semibold tracking-[-0.03em]">What it is</h2>
        <p className="mt-3 text-[17px] leading-relaxed text-[#9DA3AE]">{g.what}</p>
        <h2 className="mt-12 font-display text-2xl font-semibold tracking-[-0.03em]">{g.kind === "tool" ? "How to read it" : "The routine"}</h2>
        <ol className="mt-4 space-y-3">
          {g.steps.map((s, i) => <li key={s} className="flex gap-4 border-b border-white/[0.06] pb-3 text-[16px] leading-relaxed text-[#ECEBE4]"><span className="font-mono text-[12px] text-[#BACD1C]">{String(i + 1).padStart(2, "0")}</span>{s}</li>)}
        </ol>
        <div data-testid="guide-example" className="mt-12 border-l-2 border-[#BACD1C] bg-white/[0.025] p-6">
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#7D8390]">Real example</p>
          <p className="mt-2 text-[16px] leading-relaxed text-[#ECEBE4]">{g.example}</p>
        </div>
        <h2 className="mt-12 font-display text-2xl font-semibold tracking-[-0.03em]">Common mistakes</h2>
        <ul className="mt-4 space-y-2">{g.mistakes.map((m) => <li key={m} className="flex gap-3 text-[15px] text-[#9DA3AE]"><span className="font-mono text-[#FF4D5A]/80">✕</span>{m}</li>)}</ul>
        {g.quiz && <Quiz items={g.quiz} />}
        <a href={g.href} data-testid="guide-open-tool" className="mt-12 inline-flex items-center gap-2 bg-[#BACD1C] px-7 py-4 font-mono text-[13px] uppercase tracking-[0.14em] text-black transition-colors hover:bg-[#ECEBE4]">
          {g.kind === "tool" ? `Practice on ${g.t}` : "Open the tools"} <ArrowUpRight size={16} />
        </a>
        {!!more.length && (
          <div className="mt-20">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#5A6170]">More {g.kind === "tool" ? "tool guides" : "routines"}</p>
            <div className="mt-4 grid gap-px border border-white/[0.07] bg-white/[0.07] sm:grid-cols-3">
              {more.map((m) => <Link key={m.slug} to={`/learn/guide/${m.slug}`} className="bg-[#08090B] p-5 transition-colors hover:bg-[#0B0C0F]"><span className="block text-[15px] text-[#ECEBE4]">{m.t}</span><span className="mt-1 block text-[13px] text-[#7D8390]">{m.sub}</span></Link>)}
            </div>
          </div>
        )}
        <p className="mt-16 font-mono text-[10px] uppercase tracking-[0.2em] text-[#5A6170]">Education only · this isn't financial advice</p>
      </article>
      <Footer />
    </div>
  );
}
